import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Skeleton } from './ui/skeleton';

interface SchwabProfile {
  accounts?: Array<{
    accountId: string;
    accountNumber: string;
    accountName?: string;
    positions?: Array<{
      symbol: string;
      quantity: number;
      marketValue: number;
    }>;
  }>;
  error?: string;
  message?: string;
  code?: string;
  session?: string;
}

export default function SchwabCallbackHandler() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // const [profile, setProfile] = useState<SchwabProfile | null>(null); // No longer needed
  const [code, setCode] = useState<string | null>(null);
  const [session, setSession] = useState<string | null>(null);
  const navigate = useNavigate(); // Initialize useNavigate

  // Extract code and session from URL
  useEffect(() => {
    const url = new URL(window.location.href);
    const codeParam = url.searchParams.get('code');
    const sessionParam = url.searchParams.get('session');
    
    setCode(codeParam);
    setSession(sessionParam);
    
    if (codeParam) {
      fetchSchwabProfile(codeParam, sessionParam);
    } else {
      setError('No authorization code found in URL');
      setLoading(false);
    }
  }, []);

  const fetchSchwabProfile = async (code: string, session: string | null) => {
    try {
      setLoading(true);
      
      // Call the serverless function with the code and session parameters
      const profileEndpoint = `/api/schwab/profile?code=${encodeURIComponent(code)}${session ? `&session=${encodeURIComponent(session)}` : ''}`;
      const response = await fetch(profileEndpoint);
      
      if (!response.ok) {
        throw new Error(`Error fetching profile: ${response.status}`);
      }
      
      // const data = await response.json(); // Data not stored, just check for success
      // setProfile(data); // No longer needed
      navigate('/portfolio'); // Redirect to portfolio on success
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
      console.error('Error fetching Schwab profile:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="w-full max-w-4xl mx-auto mt-8">
        <CardHeader>
          <CardTitle><Skeleton className="h-8 w-3/4" /></CardTitle>
          <CardDescription>Processing Schwab Authorization...</CardDescription> {/* Updated text */}
        </CardHeader>
        <CardContent>
          <Skeleton className="h-48 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="w-full max-w-4xl mx-auto mt-8 border-red-200">
        <CardHeader>
          <CardTitle className="text-red-500">Error Processing Schwab Authorization</CardTitle>
          <CardDescription>
            An error occurred while processing your Schwab authorization. You can try connecting again from the portfolio page.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-red-50 p-4 rounded-md">
            <h3 className="font-bold">Error Details:</h3>
            <p>{error}</p>
            <div className="mt-4">
              <h4 className="font-semibold">URL Parameters:</h4>
              <p>Code: {code ? 'Present' : 'Not found'}</p>
              <p>Session: {session ? 'Present' : 'Not found'}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // If we have profile data with error property
  // Remove the profile display section as we are redirecting
  return null; // Or a minimal loading/success message before redirect
}